import { api, LightningElement, wire } from "lwc";
import { LabelTranslations, MultiLabelAdapter, t } from "tbme/localization";
import { getClient } from "tbme/clientAdapter";
import { CERTIFICATION_EMAIL_REQUEST_MUTATION } from "./mutation";
import {chassis} from "tbme/localState";
import { showToast } from "tds/toaster";
import { logger } from "tbme/logger";
import { requestCertificationByEmailVariables } from "./gql/requestCertificationByEmail";
import classNames from "classnames";

export default class extends LightningElement {
  private isLoading: boolean = false;
  private isSelf: boolean = false;
  private labels: LabelTranslations = undefined!;

  @api certificateId: string = "";
  @api disableAction: boolean = false;

  /** Should the action to request Certificate by email be visible */
  get isVisible() {
    return (
      this.isSelf &&
      this.certificateId
    );
  }

  @wire(MultiLabelAdapter, {
    labels: [
      "certifications.requestCertificationByEmailActionName",
      "certifications.requestCertificationByEmailError",
    ],
  })
  private handleLabels(labels: {
    certifications: LabelTranslations;
  }) {
    this.labels = {
      ...labels.certifications,
    };
  }

  private async handleCertificationEmailRequest(event: CustomEvent) {
    event.preventDefault();
    event.stopPropagation();
    const element = event.target as HTMLElement;
    if(!this.certificateId || this.isLoading || this.disableAction) {
      // If there is no certification id, we can't really take any action.
      // If the user is still waiting for the previous email request to complete, we don't want to take any action.
      // If the action is disabled (most likely beacause email request in another certification item is in progress), we don't want to take any action.
      return;
    }

    this.isLoading = true;
    this.dispatchEvent(new CustomEvent("certificationemailrequestinprogress"));

    const options = {
      variables: { certificationId: this.certificateId } as requestCertificationByEmailVariables,
      mutation: CERTIFICATION_EMAIL_REQUEST_MUTATION,
    };

    try {
      const request = () => getClient().mutate(options);
      const { data, errors } = await request();
      const { requestCertificationByEmail: { certificationEmailRequest: { status, displayMessage } } } = data;

      // "errors" are actual system errors related to the mutation request. These are unexpected and should be investigated.
      if (errors || status == "error") {
        if (errors) logger.error("errors", errors);
        showToast({
          message: displayMessage || t("certifications.requestCertificationByEmailError"),
          variant: "error",
        });
      } else if (status == "success" && displayMessage) {
        showToast({
          message: displayMessage,
          variant: "success",
        });
      }
    } catch (e) {
      logger.error("Error requesting certificate by email", e);
      showToast({
        message: t("certifications.requestCertificationByEmailError"),
        variant: "error",
      });
    } finally {
      this.isLoading = false;
      this.dispatchEvent(new CustomEvent("certificationemailrequestcompleted"));
    }
  }

  private get anchorClass() {
    const disabled = this.isLoading || this.disableAction;
    return classNames({
      "link__no-decoration-black": !disabled,
      "link__no-decoration-gray": disabled,
    });
  }

  connectedCallback() {
    const chassisData = chassis();
    this.isSelf = chassisData?.isSelf || false;
  }

}
